import request from 'oc-request';
import searchModule from './../plugins/search';
import events from './../plugins/events.js';
import NotyfUtil from './../editor/utils/notyf';
import { reinitializePlugins } from './../plugins/pluginUtils';
import { getEditor } from './../editor/core/editor';

export default () => ({
    search: null,
    documentId: null,
    isLoading: false,
    headingPaste: false,
    headingAiGenerate: false,
    headingsInput: '',

    isAddHeading: false,
    newHeading: '',
    newHeadingType: 'h2',

    eventModule: null,

    // Переменные для автосохранения
    isSaving: false,
    saveTimeout: null,
    hasChanges: false,
    saveAttempts: 0,
    maxSaveAttempts: 3,
    editorInstance: null,
    lastSaved: {
        title: '',
        status_id: null,
        content: ''
    },
    // Состояние кнопки сохранения
    saveButtonDisabled: false,

    // Переменные для прогресса SERP-анализа
    progress: 0,
    stage: 'start',
    stageName: 'Подготовка к анализу',

    // Данные документа
    document: {
        title: '',
        status_id: null,
        content: ''
    },

    // Общий метод для обработки событий
    initEvents() {
        this.documentSearchStatus();
        
        // Подписываемся на события прогресса SERP-анализа с использованием targetId
        const documentTargetId = 'document_id_' + this.documentId;
        
        this.eventModule.onTarget('serp_analysis_progress', documentTargetId, (data) => {
            // Обновляем локальные переменные прогресса
            this.progress = data.progress || 0;
            this.stage = data.stage || 'start';
            this.stageName = data.stage_name || 'Обработка запроса';
        });
        
        // Подписываемся на события ошибок SERP-анализа с использованием targetId
        this.eventModule.onTarget('serp_analysis_error', documentTargetId, (data) => {
            NotyfUtil.error(data.message || 'Произошла ошибка при анализе');
        });
        
        // Добавляем хоткей Ctrl+S
        document.addEventListener('keydown', (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'ы')) {
                e.preventDefault();
                if (this.hasChanges && !this.isSaving) {
                    this.saveDocument();
                }
            }
        });
    },

    // Методы для работы с поиском
    documentSearchStatus() {
        const documentTargetId = 'document_id_' + this.documentId;
        
        // Используем таргетную подписку вместо общей
        this.eventModule.onTarget('document_search_status', documentTargetId, (data) => {
            this.documentCancelSearch(data.message);
        });

        // Для события о недостатке монет используем обычную подписку вместо таргетной
        this.eventModule.on('user_dont_coin', (data) => {
            this.isLoading = false;
            NotyfUtil.error(data.message);
        });
    },

    documentCancelSearch(message) {
        this.isLoading = false;
        this.progress = 0;
        
        NotyfUtil.success(message);
        this.fetchPartial('documents/view/_right-bar', '.js-document-right-bar');
        reinitializePlugins();
    },

    init() {
        this.search = searchModule();
        this.eventModule = events();
        this.initDocumentData();
        this.initEvents();
        
        // Инициализация автосохранения и редактора
        this.initAutoSave();
        this.initEditor();

        // Добавляем обработчик вставки контента
        this.$el.addEventListener('insert-content', (event) => {
            const editor = getEditor();
            if (editor) {
                try {
                    // Проверяем, что это наш документ
                    if (event.detail.documentId !== this.documentId) {
                        console.log('Document ID mismatch:', event.detail.documentId, this.documentId);
                        return;
                    }
                    
                    // Обновляем данные документа
                    this.document.title = event.detail.title;
                    this.document.status_id = event.detail.status_id;
                    
                    // Вставляем текст
                    editor.commands.insertContent(event.detail.text);
                    
                    // Обновляем состояние документа
                    this.document.content = editor.getHTML();
                    this.editorInstance = editor;
                    
                    // Вызываем автосохранение напрямую
                    this.saveDocument();
                } catch (error) {
                    console.error('Error updating text:', error);
                }
            }
        });
    },

    // Инициализация данных документа
    initDocumentData() {
        // Получаем данные из data-атрибутов
        const rootElement = this.$el;
        this.document.title = rootElement.dataset.documentTitle || '';
        this.document.status_id = rootElement.dataset.documentStatus || null;
        this.documentId = rootElement.dataset.documentId || null;
    },

    fetchPartial(partial, target, data) {
        request.sendData('Ajax::onPartial', {
            data: data,
            update: { [partial]: target },
            loading: '.preloader-selector'
        });
    },

    togglePage(pageId) {
        request.sendData('SerpComponent::onToggleSerpPage', {
            data: {
                pageId: pageId
            },
            success: (response) => {
                if (response.success) {
                    this.fetchPartial('documents/view/_right-bar', '.js-document-right-bar');
                    NotyfUtil.success('Готово');
                } else {
                    NotyfUtil.error(response.error || 'Ошибка при обновлении страницы');
                }
                reinitializePlugins();
            },
            error: (xhr) => {
                NotyfUtil.error('Ошибка при обновлении страницы');
            },
            complete: () => {
                reinitializePlugins();
            }
        });
    },

    // Получение экземпляра редактора
    getEditor() {
        return getEditor();
    },

    // Генерация текста с помощью AI
    async generateAiText(documentId, title, tone, word_count, headings_structure) {
        return new Promise((resolve, reject) => {
            request.sendData('DocumentManager::onAiDocumentGenerate', {
                data: {
                    documentId: documentId, 
                    title: title, 
                    tone: tone, 
                    word_count: word_count, 
                    headings_structure: headings_structure
                },
                loading: '.preloader-selector',
                success: (response) => {
                    if (response.success && response.text) {
                        resolve(response.text);
                    } else {
                        NotyfUtil.error('Ошибка генерации');
                        reject(new Error('Ошибка генерации'));
                    }
                },
                error: (xhr) => {
                    NotyfUtil.error('Ошибка генерации');
                    reject(new Error('Ошибка генерации'));
                }
            });
        });
    },

    // Методы поиска
    async executeSearch(query, searchEngine = 'yandex', overrideDocumentId = null) {
        try {
            // Валидация запроса
            const trimmedQuery = query?.trim() || '';
            if (!this.validateSearchQuery(trimmedQuery)) {
                return;
            }

            // Получаем активный документ
            const activeDocumentId = this.getActiveDocumentId(overrideDocumentId);
            if (!activeDocumentId) {
                this.handleError('ID документа обязателен для поиска');
                return;
            }

            // Устанавливаем состояние загрузки
            this.isLoading = true;

            // Выполняем поиск
            await this.search.performSearch(
                trimmedQuery,
                activeDocumentId,
                searchEngine,
                this.handleSearchSuccess.bind(this),
                this.handleSearchError.bind(this)
            );
        } catch (error) {
            this.handleError(error);
            this.isLoading = false;
        }
    },

    async cancelSearch(overrideDocumentId = null) {
        try {
            const activeDocumentId = this.getActiveDocumentId(overrideDocumentId);
            if (!activeDocumentId) {
                this.handleError('ID документа обязателен для отмены');
                return;
            }

            request.sendData('SerpComponent::onCancelSearch', {
                data: { documentId: activeDocumentId },
                loading: '.preloader-selector',
                success: (response) => {
                    if (response.success) {
                        this.isLoading = false;
                        NotyfUtil.success('Поиск завершен досрочно');
                        this.fetchPartial('documents/view/_right-bar', '.js-document-right-bar');
                        reinitializePlugins();
                    } else if (response.error) {
                        this.handleError(response.error);
                    }
                },
                error: (xhr) => {
                    this.handleError('Не удалось отменить процесс');
                }
            });
        } catch (error) {
            this.handleError(error);
        }
    },

    validateSearchQuery(query) {
        if (!query) {
            this.handleError('Запрос не должен быть пустым');
            return false;
        }

        if (query.length < 3) {
            this.handleError('Запрос должен содержать не менее 3 символов');
            return false;
        }

        return true;
    },

    getActiveDocumentId(overrideDocumentId) {
        return overrideDocumentId || this.documentId;
    },

    handleSearchSuccess(results) {
        if (results.success && results.message) {
            NotyfUtil.success(results.message);
            this.documentSearchStatus();
        }
    },

    handleError(error) {
        const errorMessage = error?.message || error;
        console.error('Ошибка поиска:', errorMessage);
        NotyfUtil.error(errorMessage);
        this.isLoading = false;
    },

    handleSearchError(error) {
        this.handleError(error);
    },

    // Начало добавленных методов из headings.js
    // =========================================

    // Добавление нового заголовка
    addHeading() {
        if (!this.newHeading || this.newHeading.trim() === '') {
            NotyfUtil.error('Заголовок не может быть пустым');
            return;
        }

        request.sendData('DocumentManager::onHeadingCreate', {
            data: {
                heading: this.newHeading,
                type: this.newHeadingType
            },
            update: { ['documents/view/rightbar/_structure']: '.js-document-structure' },
            loading: '.preloader-selector',
            success: () => {
                NotyfUtil.success('Заголовок добавлен');
                this.isAddHeading = false;
                this.newHeading = '';
                this.newHeadingType = 'h2';
                reinitializePlugins();
            }
        });
    },

    // Редактирование заголовка
    headingEdit(documentId, tag, heading, headingId) {
        request.sendData('DocumentManager::onHeadingEdit', {
            data: {
                documentId,
                tag,
                heading,
                headingId
            },
            update: { ['documents/view/rightbar/_structure']: '.js-document-structure' },
            loading: '.preloader-selector',
            success: () => {
                NotyfUtil.success('Заголовок изменен');
                reinitializePlugins();
            }
        });
    },

    // Удаление заголовка
    headingDelete(documentId, id) {
        request.sendData('DocumentManager::onHeadingDelete', {
            data: {
                documentId,
                id
            },
            update: { ['documents/view/rightbar/_structure']: '.js-document-structure' },
            loading: '.preloader-selector',
            success: () => {
                NotyfUtil.success('Заголовок удален');
                reinitializePlugins();
            }
        });
    },

    // Генерация заголовков через AI
    aiHeadingGenerate() {
        if (this.headingAiGenerate) {
            return;
        }

        this.headingAiGenerate = true;

        request.sendData('DocumentManager::onHeadingsGenerateAi', {
            data: {headings: this.headingsInput},
            update: { ['documents/view/rightbar/_structure']: '.js-document-structure' },
            loading: '.preloader-selector',
            success: (response) => {
                if (response.success) {
                    NotyfUtil.success('Заголовки добавлены в структуру');
                    reinitializePlugins();
                }
            },
            error: (jqXHR) => {
                NotyfUtil.error('Недостаточно монет');
            },
            complete: () => {
                this.headingAiGenerate = false;
            }
        }); 
    },

    // Создание нескольких заголовков
    headingsCreate() {
        if (!this.headingsInput || this.headingsInput.trim() === '') {
            NotyfUtil.error('Напишите как минимум 1 заголовок');
            return;
        }

        request.sendData('DocumentManager::onHeadingsCreate', {
            data: {headings: this.headingsInput},
            update: { ['documents/view/rightbar/_structure']: '.js-document-structure' },
            loading: '.preloader-selector',
            success: () => {
                NotyfUtil.success('Заголовки добавлены в структуру');
                this.headingsInput = '';
                this.headingPaste = false;
            },
            complete: () => {
                reinitializePlugins();
            }
        });
    },

    // Сохранение заголовка
    headingSave(documentId, tag, heading) {
        request.sendData('DocumentManager::onHeadingSave', {
            data: {documentId: documentId, tag:tag, heading: heading},
            update: { ['documents/view/rightbar/_structure']: '.js-document-structure' },
            loading: '.preloader-selector',
            success: () => {
                NotyfUtil.success('Заголовок добавлен в структуру');
            },
            complete: () => {
                reinitializePlugins();
            }
        });
    },

    // Удаление всех заголовков
    headingDeleteAll() {
        const userConfirmed = confirm("Вы уверены, что хотите удалить все заголовки?");

        if (userConfirmed) {
            request.sendData('DocumentManager::onHeadingDeleteAll', {
                data: [],
                update: { ['documents/view/rightbar/_structure']: '.js-document-structure' },
                loading: '.preloader-selector',
                success: () => {
                    NotyfUtil.success('Заголовки удалены');
                },
                complete: () => {
                    reinitializePlugins();
                }
            });
        } 
    },
    // =========================================
    // Конец добавленных методов из headings.js
    
    // Начало оптимизированных методов автосохранения
    // =========================================
    
    // Инициализация автосохранения
    initAutoSave() {
        // Сохраняем начальные значения
        this.lastSaved = {
            title: this.document.title,
            status_id: this.document.status_id,
            content: ''  // Будет заполнено при инициализации редактора
        };
        
        // Наблюдаем за изменениями в документе через Alpine.js
        this.$watch('document', () => {
            this.hasChanges = true;
            this.scheduleAutoSave();
        }, { deep: true });
    },
    
    // Инициализация редактора и настройка отслеживания изменений
    initEditor() {
        this.$nextTick(() => {
            this.editorInstance = this.getEditor();
            if (!this.editorInstance) return;
            
            // Получаем начальное содержимое
            this.lastSaved.content = this.editorInstance.getHTML();
            this.document.content = this.lastSaved.content;
            
            // Настраиваем наблюдение за изменениями в редакторе
            this.editorInstance.on('update', ({ editor }) => {
                this.document.content = editor.getHTML();
                // Alpine автоматически отреагирует на изменение через deep watch
            });
            
            // Дополнительное сохранение при потере фокуса, если есть изменения
            this.editorInstance.on('blur', () => {
                if (this.hasChanges) {
                    this.scheduleAutoSave();
                }
            });
        });
    },
    
    // Планирование отложенного автосохранения
    scheduleAutoSave() {
        // Сбрасываем предыдущий таймер
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        
        // Устанавливаем новый таймер (3.5 секунды)
        this.saveTimeout = setTimeout(() => {
            if (this.hasChanges) {
                this.saveDocument();
            }
        }, 3500);
    },
    
    // Проверка наличия реальных изменений
    hasRealChanges() {
        return this.document.title !== this.lastSaved.title || 
               this.document.status_id !== this.lastSaved.status_id || 
               this.document.content !== this.lastSaved.content;
    },
    
    // Основной метод сохранения документа
    async saveDocument() {
        // Защита от повторных вызовов
        if (this.isSaving) return;
        
        // Проверяем, есть ли реальные изменения
        if (!this.hasRealChanges()) {
            this.hasChanges = false;
            return;
        }
        
        // Валидация данных
        if (!this.validateDocumentData()) {
            return;
        }
        
        try {
            // Устанавливаем состояния кнопки и сохранения
            this.isSaving = true;
            this.saveButtonDisabled = true; // Отключаем кнопку
            this.saveAttempts++;
            
            // Выполняем запрос на сохранение
            await request.sendData('DocumentManager::onAutoSave', {
                data: {
                    documentId: parseInt(this.documentId),
                    title: this.document.title,
                    status_id: this.document.status_id,
                    content: this.document.content
                },
                loading: '.preloader-selector',
                success: (response) => {
                    if (response && response.success) {
                        // Сохраняем текущее состояние как последнее сохраненное
                        this.lastSaved = {
                            title: this.document.title,
                            status_id: this.document.status_id,
                            content: this.document.content
                        };
                        
                        // Обновляем состояние
                        this.hasChanges = false;
                        this.saveAttempts = 0;
    
                        // Задержка перед включением кнопки
                        setTimeout(() => {
                            this.saveButtonDisabled = false;
                        }, 2000); // Оставляем кнопку disabled на 2 секунды
                    } else {
                        this.handleSaveError(response?.error || 'Ошибка при автосохранении');
                        this.saveButtonDisabled = false; // Включаем кнопку сразу в случае ошибки
                    }
                },
                error: (xhr, status, error) => {
                    this.handleSaveError('Ошибка при автосохранении: ' + (error || 'Неизвестная ошибка'));
                    this.saveButtonDisabled = false; // Включаем кнопку сразу в случае ошибки
                }
            });
        } catch (error) {
            this.handleSaveError('Ошибка при автосохранении: ' + (error.message || 'Неизвестная ошибка'));
            this.saveButtonDisabled = false; // Включаем кнопку сразу в случае ошибки
        } finally {
            this.isSaving = false;
            // Заметьте, что saveButtonDisabled остается true еще на 2 секунды в случае успеха
        }
    },
    
    // Обработка ошибок сохранения
    handleSaveError(errorMessage) {
        if (this.saveAttempts < this.maxSaveAttempts) {
            // Пробуем сохранить снова через 5 секунд
            setTimeout(() => {
                this.saveDocument();
            }, 5000);
        } else {
            NotyfUtil.error(errorMessage);
            this.saveAttempts = 0;
        }
    },
    
    // Валидация данных документа
    validateDocumentData() {
        if (!this.documentId) {
            NotyfUtil.error('ID документа обязателен');
            return false;
        }
        
        if (!this.document.title) {
            NotyfUtil.error('Заголовок не может быть пустым');
            return false;
        }
        
        if (!this.document.status_id) {
            NotyfUtil.error('Статус не может быть пустым');
            return false;
        }
        
        return true;
    }
    // =========================================
    // Конец оптимизированных методов автосохранения
});
